import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, UserX } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import z from "zod";
import { toast } from "react-toastify";
import { VerificationCodeInput } from "@/components/auth/verification-code-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { trackUserLoggedIn, trackUserSignedUp } from "@/lib/analytics-events";
import { authService } from "@/lib/auth-service";
import { API_BASE_URL } from "@/lib/consts";

// --- Types ---

interface GoogleCallbackSuccess {
	status: "google_linked";
	email: string | null;
	next_step: string;
	invite_token: string | null;
	access_token: string | null;
	refresh_token: string | null;
	expires_in: number | null;
	campaign_slug: string | null;
}

// --- Route definition ---

export const Route = createFileRoute("/auth/google-callback")({
	component: GoogleCallbackPage,
	validateSearch: (search) =>
		z
			.object({
				code: z.string().min(1).optional(),
				state: z.string().min(1).optional(),
				error: z.string().optional(),
			})
			.parse(search),
});

// --- Main component ---

type UiState =
	| { phase: "processing" }
	| { phase: "signup_success"; data: GoogleCallbackSuccess }
	| { phase: "login_2fa"; sessionToken: string; phoneMasked: string }
	| { phase: "error"; status: string; message: string; backTo: "/" | "/login" };

function GoogleCallbackPage() {
	const { code, state, error: oauthError } = Route.useSearch();
	const navigate = useNavigate();
	const [uiState, setUiState] = useState<UiState>({ phase: "processing" });
	const processedRef = useRef(false);

	// --- 2FA state (login flow) ---
	const [codeError, setCodeError] = useState("");
	const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
	const [lockoutSeconds, setLockoutSeconds] = useState(0);
	const [verifyLoading, setVerifyLoading] = useState(false);
	const [resendLoading, setResendLoading] = useState(false);

	// --- Signup flow handler ---
	const processSignupCallback = useCallback(
		async (code: string, state: string) => {
			try {
				const res = await fetch(`${API_BASE_URL}/auth/google/callback`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ code, state }),
				});

				if (res.ok) {
					const data: GoogleCallbackSuccess = await res.json();
					trackUserSignedUp("google");
					if (data.access_token) {
						authService.setTokens({
							authToken: data.access_token,
							refreshToken: data.refresh_token ?? "",
							expiry: data.expires_in ?? 1800,
						});
					}
					setUiState({ phase: "signup_success", data });

					setTimeout(() => {
						navigate({
							to: "/auth/phone-verification",
							search: { token: data.invite_token ?? undefined },
						});
					}, 1500);
					return;
				}

				const body = await res.json().catch(() => null);
				const detail = body?.detail;

				setUiState({
					phase: "error",
					status: detail?.status || "error",
					message: detail?.message || "Something went wrong. Please try again.",
					backTo: "/",
				});
			} catch {
				setUiState({
					phase: "error",
					status: "error",
					message: "Network error. Please check your connection and try again.",
					backTo: "/",
				});
			}
		},
		[navigate],
	);

	// --- Login flow handler ---
	const processLoginCallback = useCallback(
		async (code: string, state: string) => {
			try {
				const res = await fetch(`${API_BASE_URL}/auth/google/login`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ code, state }),
				});

				const data = await res.json().catch(() => null);

				if (!res.ok) {
					setUiState({
						phase: "error",
						status: "login_failed",
						message: data?.detail || "Login failed. Please try again.",
						backTo: "/login",
					});
					return;
				}

				if (data.requires_2fa) {
					setUiState({
						phase: "login_2fa",
						sessionToken: data.session_token,
						phoneMasked: data.phone_masked,
					});
					return;
				}

				// Direct login
				authService.setTokens({
					authToken: data.access_token,
					refreshToken: data.refresh_token,
					expiry: data.expires_in,
				});
				trackUserLoggedIn("google");
				toast.success(data.message || "Login successful!");
				navigate({ to: "/" });
			} catch {
				setUiState({
					phase: "error",
					status: "error",
					message: "Network error. Please check your connection and try again.",
					backTo: "/login",
				});
			}
		},
		[navigate],
	);

	// --- Routing effect ---
	useEffect(() => {
		if (processedRef.current) return;
		processedRef.current = true;

		const flow = localStorage.getItem("google_auth_flow");
		localStorage.removeItem("google_auth_flow");
		const isLoginFlow = flow === "login";

		if (oauthError) {
			setUiState({
				phase: "error",
				status: "oauth_denied",
				message:
					oauthError === "access_denied"
						? "Google sign-in was cancelled."
						: `Google sign-in failed: ${oauthError}`,
				backTo: isLoginFlow ? "/login" : "/",
			});
			return;
		}

		if (!code || !state) {
			setUiState({
				phase: "error",
				status: "missing_params",
				message: "Invalid callback. Please try signing in again.",
				backTo: isLoginFlow ? "/login" : "/",
			});
			return;
		}

		if (isLoginFlow) {
			processLoginCallback(code, state);
		} else {
			processSignupCallback(code, state);
		}
	}, [code, state, oauthError, processSignupCallback, processLoginCallback]);

	// --- Processing ---
	if (uiState.phase === "processing") {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="flex flex-col items-center gap-4">
					<Spinner className="size-8 text-primary" />
					<p className="text-muted-foreground text-sm">
						Connecting your Google account...
					</p>
				</div>
			</div>
		);
	}

	// --- Signup success ---
	if (uiState.phase === "signup_success") {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="flex flex-col items-center gap-4 text-center">
					<div className="size-12 rounded-full bg-green-100 flex items-center justify-center">
						<svg
							className="size-6 text-green-600"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={2}
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M4.5 12.75l6 6 9-13.5"
							/>
						</svg>
					</div>
					<div className="space-y-1">
						<p className="font-medium text-foreground">Google account connected</p>
						<p className="text-sm text-muted-foreground">
							Continuing to the next step...
						</p>
					</div>
					<Spinner className="size-4 text-muted-foreground" />
				</div>
			</div>
		);
	}

	// --- Login 2FA ---
	if (uiState.phase === "login_2fa") {
		return (
			<div className="min-h-screen flex items-center justify-center px-6">
				<div className="w-full max-w-sm">
					<Card>
						<CardContent className="pt-6">
							<div className="flex flex-col gap-6">
								<div className="flex flex-col items-center gap-1 text-center">
									<h1 className="text-2xl font-bold">
										Verify Your Identity
									</h1>
								</div>

								<VerificationCodeInput
									phoneMasked={uiState.phoneMasked}
									loading={verifyLoading}
									resendLoading={resendLoading}
									error={codeError}
									remainingAttempts={remainingAttempts}
									lockoutSeconds={lockoutSeconds}
									initialCooldown={60}
									onSubmit={async (code) => {
										setVerifyLoading(true);
										setCodeError("");

										try {
											const res = await fetch(
												`${API_BASE_URL}/auth/2fa/verify`,
												{
													method: "POST",
													headers: {
														"Content-Type": "application/json",
													},
													body: JSON.stringify({
														session_token: uiState.sessionToken,
														code,
													}),
												},
											);

											if (res.ok) {
												const data = await res.json();
												authService.setTokens({
													authToken: data.access_token,
													refreshToken: data.refresh_token,
													expiry: data.expires_in,
												});
												trackUserLoggedIn("google");
												toast.success("Login successful!");
												navigate({ to: "/" });
												return;
											}

											const body = await res.json().catch(() => null);
											const detail = body?.detail;

											if (detail?.status === "locked_out") {
												setLockoutSeconds(detail.lockout_seconds || 900);
												setCodeError("Too many incorrect attempts.");
											} else if (detail?.status === "wrong_code") {
												setRemainingAttempts(detail.remaining_attempts);
												setCodeError(
													`Incorrect code. ${detail.remaining_attempts} attempt${detail.remaining_attempts === 1 ? "" : "s"} remaining.`,
												);
											} else if (
												detail?.status === "session_expired" ||
												detail?.status === "session_used"
											) {
												toast.error("Session expired. Please log in again.");
												navigate({ to: "/login" });
											} else {
												setCodeError(
													detail?.message ||
														"Verification failed. Please try again.",
												);
											}
										} catch {
											setCodeError("Network error. Please try again.");
										} finally {
											setVerifyLoading(false);
										}
									}}
									onResend={async () => {
										setResendLoading(true);
										setCodeError("");

										try {
											const res = await fetch(
												`${API_BASE_URL}/auth/2fa/resend`,
												{
													method: "POST",
													headers: {
														"Content-Type": "application/json",
													},
													body: JSON.stringify({
														session_token: uiState.sessionToken,
													}),
												},
											);

											if (!res.ok) {
												const body = await res.json().catch(() => null);
												if (body?.detail?.status === "session_expired") {
													toast.error(
														"Session expired. Please log in again.",
													);
													navigate({ to: "/login" });
												}
											}
										} catch {
											// Silently fail -- user can retry
										} finally {
											setResendLoading(false);
										}
									}}
								/>

								<Link
									to="/login"
									className="inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
								>
									<ArrowLeft className="size-3.5" />
									Back to login
								</Link>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	// --- Error state ---
	const isAlreadyLinked = uiState.status === "already_linked";
	const backTo = uiState.backTo;

	return (
		<div className="min-h-screen flex items-center justify-center px-6">
			<div className="w-full max-w-sm text-center space-y-6">
				<div className="flex justify-center">
					{isAlreadyLinked ? (
						<UserX className="size-8 text-amber-500" />
					) : (
						<AlertCircle className="size-8 text-destructive" />
					)}
				</div>
				<div className="space-y-2">
					<h1 className="text-2xl font-semibold text-foreground">
						{isAlreadyLinked
							? "Google account already in use"
							: "Sign-in failed"}
					</h1>
					<p className="text-muted-foreground text-[15px]">{uiState.message}</p>
				</div>
				<Button
					size="lg"
					variant="outline"
					className="w-full gap-2"
					onClick={() => {
						if (backTo === "/login") {
							navigate({ to: "/login" });
						} else {
							window.history.back();
						}
					}}
				>
					<ArrowLeft className="size-4" />
					{backTo === "/login" ? "Back to login" : "Go back to sign-in options"}
				</Button>
			</div>
		</div>
	);
}
